import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Database, RefreshCw, ShieldCheck, AlertTriangle, ArrowRightLeft } from "lucide-react";

export default function MigrationPanel() {
  const [status, setStatus] = useState(null);
  const [cands, setCands] = useState([]);
  const [sel, setSel] = useState(new Set());
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([api.get("/superadmin/migration/status"), api.get("/superadmin/migration/seed-candidates")])
    .then(([s, c]) => { setStatus(s.data); setCands(c.data.candidates); })
    .catch(() => {});
  useEffect(() => { load(); }, []);

  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post("/superadmin/migration/run"); setStatus(data); toast.success(data.complete ? "All legacy records are assigned to GADTECH Concrete" : "Stamping re-run — some records still unassigned"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const quarantine = async () => {
    if (!sel.size) return toast.error("Tick the dummy customers to move first");
    if (!window.confirm(`Move ${sel.size} dummy customer(s) and everything linked to them into ArmorBatch Sample Plant? Docket/invoice numbering is untouched.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/migration/quarantine", { customer_ids: [...sel] });
      toast.success(`Moved ${Object.keys(data.moved).length} customer(s) to the Sample Plant`);
      setSel(new Set()); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  if (!status) return null;
  const cols = Object.entries(status.collections || {});
  return (
    <div className="border border-industrial bg-surface" data-testid="migration-panel">
      <div className="px-5 py-4 border-b border-industrial flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-primary-orange" />
          <div>
            <div className="label-tag">LEGACY DATA MIGRATION · GADTECH CONCRETE</div>
            <h2 className="heading font-black uppercase text-base md:text-lg">Customers · Trucks · Mixes · Pricing · Dockets · Invoices</h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span data-testid="migration-complete" className={`mono text-[10px] uppercase tracking-widest px-2 py-1 border flex items-center gap-1 ${status.complete ? "border-emerald-500 text-emerald-500" : "border-amber-500 text-amber-400"}`}>
            {status.complete ? <ShieldCheck className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}{status.complete ? "all records assigned" : "unassigned records found"}
          </span>
          <Button data-testid="migration-run" size="sm" variant="outline" className="rounded-none" disabled={busy} onClick={run}><RefreshCw className="w-3.5 h-3.5 mr-1" />Re-run stamping</Button>
        </div>
      </div>
      <div className="p-5 grid grid-cols-1 xl:grid-cols-2 gap-5">
        <div>
          <div className="grid grid-cols-3 gap-2 mb-3">
            <div className="border border-industrial p-2"><div className="label-tag">Docket range (preserved)</div><div className="mono text-xs font-bold" data-testid="migration-docket-range">{status.docket_range.first || "—"} → {status.docket_range.last || "—"}</div></div>
            <div className="border border-industrial p-2"><div className="label-tag">Last invoice no.</div><div className="mono text-xs font-bold" data-testid="migration-last-invoice">{status.last_invoice_no || "—"}</div></div>
            <div className="border border-industrial p-2"><div className="label-tag">Counters</div><div className="mono text-[10px]" data-testid="migration-counters">{status.counters.filter((c) => c.id.startsWith("org_gadtech")).map((c) => `${c.id.split(":")[1]}=${c.seq}`).join(" · ") || "—"}</div></div>
          </div>
          <table className="w-full text-xs mono" data-testid="migration-collections">
            <thead><tr className="label-tag text-left border-b border-industrial"><th className="py-1">Collection</th><th className="text-right">Total</th><th className="text-right">GADTECH</th><th className="text-right">Other orgs</th><th className="text-right">Unassigned</th></tr></thead>
            <tbody>
              {cols.map(([n, c]) => (
                <tr key={n} className="border-b border-industrial/40" data-testid={`migration-col-${n}`}>
                  <td className="py-1">{n}</td><td className="text-right">{c.total}</td><td className="text-right text-emerald-500">{c.gadtech}</td><td className="text-right text-muted-industrial">{c.other_orgs}</td>
                  <td className={`text-right ${c.unassigned ? "text-red-500 font-bold" : ""}`}>{c.unassigned}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="label-tag">SEED / TEST DUMMIES STILL IN GADTECH ({cands.length})</div>
            <Button data-testid="migration-quarantine" size="sm" className="rounded-none bg-sky-500 hover:bg-sky-400 text-black" disabled={busy || !sel.size} onClick={quarantine}>
              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />Move {sel.size || ""} to Sample Plant
            </Button>
          </div>
          <p className="text-xs text-muted-industrial mb-2">Dry run — nothing moves until you tick and confirm. Moving keeps every docket & invoice number exactly as issued; only the owning organization changes.</p>
          <div className="max-h-72 overflow-y-auto border border-industrial">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-surface"><tr className="label-tag text-left border-b border-industrial"><th className="p-1.5"><input type="checkbox" data-testid="migration-select-all" checked={!!cands.length && sel.size === cands.length} onChange={(e) => setSel(e.target.checked ? new Set(cands.map((c) => c.id)) : new Set())} /></th><th>Customer</th><th className="text-right">Jobs</th><th className="text-right">Dockets</th><th className="text-right">Invoices</th><th className="text-right">Cash</th></tr></thead>
              <tbody>
                {cands.map((c) => (
                  <tr key={c.id} className="border-b border-industrial/40" data-testid={`migration-cand-${c.id}`}>
                    <td className="p-1.5"><input type="checkbox" data-testid={`migration-cand-cb-${c.id}`} checked={sel.has(c.id)} onChange={() => toggle(c.id)} /></td>
                    <td className="truncate max-w-[220px]">{c.name}</td><td className="text-right mono">{c.jobs}</td><td className="text-right mono">{c.dockets}</td><td className="text-right mono">{c.invoices}</td><td className="text-right mono">{c.cash_sales}</td>
                  </tr>
                ))}
                {!cands.length && <tr><td colSpan={6} className="p-3 text-center text-muted-industrial" data-testid="migration-cands-empty">No seed/test dummies left in GADTECH Concrete.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
